<script setup lang="ts">
import { useToast } from '@/shared/composables/UseToast'
import {
  useAdminListHardSkills,
  useAdminUpdateHardSkill,
  useAdminPublishHardSkill,
  useAdminUnpublishHardSkill,
  useAdminListCertificates,
  useAdminUpdateCertificate,
  useAdminPublishCertificate,
  useAdminUnpublishCertificate,
  getAdminListHardSkillsQueryKey,
  getAdminListCertificatesQueryKey,
  useListJobCategories,
  useAdminListSkillCategories,
} from '@swipe4work/api-orval'
import type {
  AdminHardSkillCrudHardSkillDto,
  AdminCertificateCrudCertificateDto,
} from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useConfirm } from 'primevue/useconfirm'
import { FilterMatchMode, FilterService } from '@primevue/core'

definePage({
  path: '/data/hard-skills',
  name: '/data/HardSkills',
  meta: {
    title: 'Skills & Certificaten',
  },
})

FilterService.register('containsCategoryId', (value: number[] | null, filter: number | null) => {
  if (filter === null || filter === undefined) {
    return true
  }

  return value?.includes(filter) ?? false
})

const toast = useToast()
const confirm = useConfirm()
const queryClient = useQueryClient()
const activeTab = ref<'skills' | 'certificates'>('skills')

// Publish/Draft status is a binary flag on the Skill (isDraft). Options drive the status filter.
const statusFilterOptions = [
  {
    label: 'Concept',
    value: true, 
  },
  {
    label: 'Gepubliceerd',
    value: false, 
  },
]

// --- Shared data ---
const { data: categoriesData } = useListJobCategories()
const { data: skillCategoriesData } = useAdminListSkillCategories()
const categories = computed(() => categoriesData.value?.jobCategories ?? [])
const skillCategories = computed(() => skillCategoriesData.value?.skillCategories ?? [])

function getCategoryNames(ids: number[]): string {
  return ids
    .map(id => categories.value.find(c => c.id === id)?.name ?? String(id))
    .join(', ')
}

function getSkillCategoryName(id: number | null | undefined): string {
  if (!id) {
    return '-'
  }

  return skillCategories.value.find((c) => c.id === id)?.name ?? String(id)
}

// --- Skills ---
const skillFilters = ref({
  global: {
    value: null as string | null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  jobCategoryIds: {
    value: null as number | null,
    matchMode: 'containsCategoryId',
  },
  skillCategoryId: {
    value: null as number | null,
    matchMode: FilterMatchMode.EQUALS,
  },
  isDraft: {
    value: null as boolean | null,
    matchMode: FilterMatchMode.EQUALS,
  },
})

const { data: hardSkillsData, isPending: skillsPending, isError: skillsError } = useAdminListHardSkills()
const hardSkills = computed(() => hardSkillsData.value?.hardSkills ?? [])

const editingSkill = ref<AdminHardSkillCrudHardSkillDto | null>(null)
const editSkillName = ref('')
const editSkillDescription = ref<string | null>(null)
const editJobCategoryIds = ref<number[]>([])
const editSkillCategoryId = ref<number | null>(null)

function startEditSkill(skill: AdminHardSkillCrudHardSkillDto) {
  editingSkill.value = skill
  editSkillName.value = skill.name
  editSkillDescription.value = skill.description ?? null
  editJobCategoryIds.value = [ ...skill.jobCategoryIds ]
  editSkillCategoryId.value = skill.skillCategoryId ?? null
}

function cancelEditSkill() {
  editingSkill.value = null
}

const updateSkillMutation = useAdminUpdateHardSkill({
  mutation: {
    onSuccess: () => {
      toast.success('Bijgewerkt', 'De vaardigheid is bijgewerkt.')
      queryClient.invalidateQueries({ queryKey: getAdminListHardSkillsQueryKey() })
      editingSkill.value = null
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het bijwerken.')
    },
  },
})

function submitEditSkill() {
  if (!editingSkill.value || !editSkillName.value.trim() || editJobCategoryIds.value.length === 0) {
    return
  }

  updateSkillMutation.mutate({
    data: {
      id: editingSkill.value.id,
      name: editSkillName.value.trim(),
      description: editSkillDescription.value ?? null,
      jobCategoryIds: editJobCategoryIds.value,
      skillCategoryId: editSkillCategoryId.value ?? undefined,
    },
  })
}

function skillPublishMutationOptions(pastTense: string) {
  return {
    mutation: {
      onSuccess: () => {
        toast.success('Gelukt', `De skill is ${pastTense}.`)
        queryClient.invalidateQueries({ queryKey: getAdminListHardSkillsQueryKey() })
      },
      onError: () => {
        toast.error('Fout', 'Er is iets misgegaan bij het wijzigen van de status.')
      },
    },
  }
}

const publishSkillMutation = useAdminPublishHardSkill(skillPublishMutationOptions('gepubliceerd'))
const unpublishSkillMutation = useAdminUnpublishHardSkill(skillPublishMutationOptions('gedepubliceerd'))

function confirmToggleSkillPublish(skill: AdminHardSkillCrudHardSkillDto) {
  const willPublish = skill.isDraft

  confirm.require({
    header: willPublish ? 'Skill publiceren?' : 'Skill depubliceren?',
    message: willPublish
      ? `Weet je zeker dat je "${skill.name}" wilt publiceren? De skill wordt zichtbaar voor kandidaten.`
      : `Weet je zeker dat je "${skill.name}" wilt depubliceren? De skill verdwijnt uit de catalogus voor kandidaten. Kandidaten die de skill al op hun profiel hebben, behouden deze.`,
    icon: 'fa-regular fa-triangle-exclamation',
    acceptLabel: willPublish ? 'Publiceren' : 'Depubliceren',
    rejectLabel: 'Annuleren',
    accept: () => {
      const mutation = willPublish ? publishSkillMutation : unpublishSkillMutation

      mutation.mutate({ id: skill.id })
    },
  })
}

// --- Certificates ---
const certFilters = ref({
  global: {
    value: null as string | null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  skillCategoryId: {
    value: null as number | null,
    matchMode: FilterMatchMode.EQUALS,
  },
  isDraft: {
    value: null as boolean | null,
    matchMode: FilterMatchMode.EQUALS,
  },
})

const { data: certificatesData, isPending: certsPending, isError: certsError } = useAdminListCertificates()
const certificates = computed(() => certificatesData.value?.certificates ?? [])

const editingCert = ref<AdminCertificateCrudCertificateDto | null>(null)
const editCertName = ref('')
const editCertDescription = ref<string | null>(null)
const editCertSkillCategoryId = ref<number | null>(null)
const editCertJobCategoryIds = ref<number[]>([])

function startEditCert(cert: AdminCertificateCrudCertificateDto) {
  editingCert.value = cert
  editCertName.value = cert.name
  editCertDescription.value = cert.description ?? null
  editCertSkillCategoryId.value = cert.skillCategoryId ?? null
  editCertJobCategoryIds.value = [ ...cert.jobCategoryIds ]
}

function cancelEditCert() {
  editingCert.value = null
}

const updateCertMutation = useAdminUpdateCertificate({
  mutation: {
    onSuccess: () => {
      toast.success('Bijgewerkt', 'Het certificaat is bijgewerkt.')
      queryClient.invalidateQueries({ queryKey: getAdminListCertificatesQueryKey() })
      editingCert.value = null
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het bijwerken.')
    },
  },
})

function submitEditCert() {
  if (!editingCert.value || !editCertName.value.trim()) {
    return
  }

  updateCertMutation.mutate({
    data: {
      id: editingCert.value.id,
      name: editCertName.value.trim(),
      description: editCertDescription.value ?? null,
      skillCategoryId: editCertSkillCategoryId.value ?? undefined,
      jobCategoryIds: editCertJobCategoryIds.value,
    },
  })
}

function certPublishMutationOptions(pastTense: string) {
  return {
    mutation: {
      onSuccess: () => {
        toast.success('Gelukt', `Het certificaat is ${pastTense}.`)
        queryClient.invalidateQueries({ queryKey: getAdminListCertificatesQueryKey() })
      },
      onError: () => {
        toast.error('Fout', 'Er is iets misgegaan bij het wijzigen van de status.')
      },
    },
  }
}

const publishCertMutation = useAdminPublishCertificate(certPublishMutationOptions('gepubliceerd'))
const unpublishCertMutation = useAdminUnpublishCertificate(certPublishMutationOptions('gedepubliceerd'))

function confirmToggleCertPublish(cert: AdminCertificateCrudCertificateDto) {
  const willPublish = cert.isDraft

  confirm.require({
    header: willPublish ? 'Certificaat publiceren?' : 'Certificaat depubliceren?',
    message: willPublish
      ? `Weet je zeker dat je "${cert.name}" wilt publiceren? Het certificaat wordt zichtbaar voor kandidaten.`
      : `Weet je zeker dat je "${cert.name}" wilt depubliceren? Het certificaat verdwijnt uit de catalogus voor kandidaten. Kandidaten die het al op hun profiel hebben, behouden het.`,
    icon: 'fa-regular fa-triangle-exclamation',
    acceptLabel: willPublish ? 'Publiceren' : 'Depubliceren',
    rejectLabel: 'Annuleren',
    accept: () => {
      const mutation = willPublish ? publishCertMutation : unpublishCertMutation

      mutation.mutate({ id: cert.id })
    },
  })
}
</script>

<template>
  <div class="space-y-5">
    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab value="skills">
          Skills
        </Tab>
        <Tab value="certificates">
          Certificaten
        </Tab>
      </TabList>

      <!-- Skills tab -->
      <TabPanel value="skills">
        <Card>
          <template #content>
            <div class="flex flex-col gap-4">
              <h3 class="text-lg font-semibold">
                Skills
              </h3>

              <IconField>
                <InputIcon class="fa-regular fa-search" />
                <InputText v-model="skillFilters.global.value" placeholder="Zoeken..." class="w-full" />
              </IconField>

              <div v-if="skillsPending" class="flex flex-col gap-2">
                <Skeleton v-for="n in 5" :key="n" height="3rem" />
              </div>
              <Message v-else-if="skillsError" severity="error">
                Er is iets misgegaan bij het laden van de skills.
              </Message>
              <DataTable
                v-else
                v-model:filters="skillFilters"
                :value="hardSkills"
                :global-filter-fields="['name']"
                filter-display="menu"
                sort-field="name"
                :sort-order="1"
                class="w-full"
              >
                <Column field="name" header="Naam" sortable style="width: 180px">
                  <template #body="{ data: skill }">
                    <div v-if="editingSkill?.id === skill.id">
                      <InputText v-model="editSkillName" class="w-full" />
                    </div>
                    <span v-else>{{ skill.name }}</span>
                  </template>
                </Column>
                <Column field="description" header="Beschrijving">
                  <template #body="{ data: skill }">
                    <div v-if="editingSkill?.id === skill.id">
                      <InputText v-model="editSkillDescription" placeholder="Optioneel" class="w-full" />
                    </div>
                    <span v-else class="text-sm text-surface-500">{{ skill.description ?? '-' }}</span>
                  </template>
                </Column>
                <Column
                  header="Beroepsgroepen"
                  filter-field="jobCategoryIds"
                  :show-filter-match-modes="false"
                >
                  <template #body="{ data: skill }">
                    <div v-if="editingSkill?.id === skill.id">
                      <MultiSelect
                        v-model="editJobCategoryIds"
                        :options="categories"
                        option-label="name"
                        option-value="id"
                        placeholder="Selecteer beroepsgroepen"
                        class="w-full"
                      />
                    </div>
                    <span v-else>{{ getCategoryNames(skill.jobCategoryIds) }}</span>
                  </template>
                  <template #filter="{ filterModel, filterCallback }">
                    <Select
                      v-model="filterModel.value"
                      :options="categories"
                      option-label="name"
                      option-value="id"
                      placeholder="Alles"
                      show-clear
                      @change="filterCallback()"
                    />
                  </template>
                </Column>
                <Column
                  header="Categorie"
                  sortable
                  sort-field="skillCategoryId"
                  filter-field="skillCategoryId"
                  :show-filter-match-modes="false"
                >
                  <template #body="{ data: skill }">
                    <div v-if="editingSkill?.id === skill.id">
                      <Select
                        v-model="editSkillCategoryId"
                        :options="skillCategories"
                        option-label="name"
                        option-value="id"
                        placeholder="Optioneel"
                        class="w-full"
                        show-clear
                      />
                    </div>
                    <span v-else>{{ getSkillCategoryName(skill.skillCategoryId) }}</span>
                  </template>
                  <template #filter="{ filterModel, filterCallback }">
                    <Select
                      v-model="filterModel.value"
                      :options="skillCategories"
                      option-label="name"
                      option-value="id"
                      placeholder="Alles"
                      show-clear
                      @change="filterCallback()"
                    />
                  </template>
                </Column>
                <Column
                  header="Status"
                  sortable
                  sort-field="isDraft"
                  filter-field="isDraft"
                  :show-filter-match-modes="false"
                  style="width: 140px"
                >
                  <template #body="{ data: skill }">
                    <Tag
                      :severity="skill.isDraft ? 'secondary' : 'success'"
                      :value="skill.isDraft ? 'Concept' : 'Gepubliceerd'"
                    />
                  </template>
                  <template #filter="{ filterModel, filterCallback }">
                    <Select
                      v-model="filterModel.value"
                      :options="statusFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Alles"
                      show-clear
                      @change="filterCallback()"
                    />
                  </template>
                </Column>
                <Column header="Acties" style="width: 150px">
                  <template #body="{ data: skill }">
                    <div v-if="editingSkill?.id === skill.id" class="flex gap-2">
                      <Button
                        icon="fa-regular fa-check"
                        severity="success"
                        size="small"
                        v-tooltip.top="'Opslaan'"
                        :loading="updateSkillMutation.isPending.value"
                        @click="submitEditSkill"
                      />
                      <Button
                        icon="fa-regular fa-xmark"
                        severity="secondary"
                        size="small"
                        v-tooltip.top="'Annuleren'"
                        @click="cancelEditSkill"
                      />
                    </div>
                    <div v-else class="flex gap-2">
                      <Button
                        icon="fa-regular fa-pen"
                        severity="secondary"
                        size="small"
                        v-tooltip.top="'Bewerken'"
                        @click="startEditSkill(skill)"
                      />
                      <Button
                        :icon="skill.isDraft ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'"
                        :severity="skill.isDraft ? 'success' : 'secondary'"
                        size="small"
                        v-tooltip.top="skill.isDraft ? 'Publiceren' : 'Depubliceren'"
                        @click="confirmToggleSkillPublish(skill)"
                      />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </TabPanel>

      <!-- Certificates tab -->
      <TabPanel value="certificates">
        <Card>
          <template #content>
            <div class="flex flex-col gap-4">
              <h3 class="text-lg font-semibold">
                Certificaten
              </h3>

              <IconField>
                <InputIcon class="fa-regular fa-search" />
                <InputText v-model="certFilters.global.value" placeholder="Zoeken..." class="w-full" />
              </IconField>

              <div v-if="certsPending" class="flex flex-col gap-2">
                <Skeleton v-for="n in 5" :key="n" height="3rem" />
              </div>
              <Message v-else-if="certsError" severity="error">
                Er is iets misgegaan bij het laden van de certificaten.
              </Message>
              <DataTable
                v-else
                v-model:filters="certFilters"
                :value="certificates"
                :global-filter-fields="['name', 'description']"
                filter-display="menu"
                sort-field="name"
                :sort-order="1"
                class="w-full"
              >
                <Column field="name" header="Naam" sortable style="width: 180px">
                  <template #body="{ data: cert }">
                    <div v-if="editingCert?.id === cert.id">
                      <InputText v-model="editCertName" class="w-full" />
                    </div>
                    <span v-else>{{ cert.name }}</span>
                  </template>
                </Column>
                <Column field="description" header="Beschrijving (LLM-context)">
                  <template #body="{ data: cert }">
                    <div v-if="editingCert?.id === cert.id">
                      <InputText v-model="editCertDescription" placeholder="Bijv. Vereist in bouw en techniek" class="w-full" />
                    </div>
                    <span v-else class="text-sm text-surface-500">{{ cert.description ?? '-' }}</span>
                  </template>
                </Column>
                <Column
                  header="Categorie"
                  sortable
                  sort-field="skillCategoryId"
                  filter-field="skillCategoryId"
                  :show-filter-match-modes="false"
                  style="width: 180px"
                >
                  <template #body="{ data: cert }">
                    <div v-if="editingCert?.id === cert.id">
                      <Select
                        v-model="editCertSkillCategoryId"
                        :options="skillCategories"
                        option-label="name"
                        option-value="id"
                        placeholder="Optioneel"
                        class="w-full"
                        show-clear
                      />
                    </div>
                    <span v-else>{{ getSkillCategoryName(cert.skillCategoryId) }}</span>
                  </template>
                  <template #filter="{ filterModel, filterCallback }">
                    <Select
                      v-model="filterModel.value"
                      :options="skillCategories"
                      option-label="name"
                      option-value="id"
                      placeholder="Alles"
                      show-clear
                      @change="filterCallback()"
                    />
                  </template>
                </Column>
                <Column
                  header="Beroepsgroepen"
                  filter-field="jobCategoryIds"
                  :show-filter-match-modes="false"
                >
                  <template #body="{ data: cert }">
                    <div v-if="editingCert?.id === cert.id">
                      <MultiSelect
                        v-model="editCertJobCategoryIds"
                        :options="categories"
                        option-label="name"
                        option-value="id"
                        placeholder="Alle sectoren (geen koppeling)"
                        class="w-full"
                      />
                    </div>
                    <span v-else>{{ cert.jobCategoryIds.length ? getCategoryNames(cert.jobCategoryIds) : '-' }}</span>
                  </template>
                  <template #filter="{ filterModel, filterCallback }">
                    <Select
                      v-model="filterModel.value"
                      :options="categories"
                      option-label="name"
                      option-value="id"
                      placeholder="Alles"
                      show-clear
                      @change="filterCallback()"
                    />
                  </template>
                </Column>
                <Column
                  header="Status"
                  sortable
                  sort-field="isDraft"
                  filter-field="isDraft"
                  :show-filter-match-modes="false"
                  style="width: 140px"
                >
                  <template #body="{ data: cert }">
                    <Tag
                      :severity="cert.isDraft ? 'secondary' : 'success'"
                      :value="cert.isDraft ? 'Concept' : 'Gepubliceerd'"
                    />
                  </template>
                  <template #filter="{ filterModel, filterCallback }">
                    <Select
                      v-model="filterModel.value"
                      :options="statusFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Alles"
                      show-clear
                      @change="filterCallback()"
                    />
                  </template>
                </Column>
                <Column header="Acties" style="width: 150px">
                  <template #body="{ data: cert }">
                    <div v-if="editingCert?.id === cert.id" class="flex gap-2">
                      <Button
                        icon="fa-regular fa-check"
                        severity="success"
                        size="small"
                        v-tooltip.top="'Opslaan'"
                        :loading="updateCertMutation.isPending.value"
                        @click="submitEditCert"
                      />
                      <Button
                        icon="fa-regular fa-xmark"
                        severity="secondary"
                        size="small"
                        v-tooltip.top="'Annuleren'"
                        @click="cancelEditCert"
                      />
                    </div>
                    <div v-else class="flex gap-2">
                      <Button
                        icon="fa-regular fa-pen"
                        severity="secondary"
                        size="small"
                        v-tooltip.top="'Bewerken'"
                        @click="startEditCert(cert)"
                      />
                      <Button
                        :icon="cert.isDraft ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'"
                        :severity="cert.isDraft ? 'success' : 'secondary'"
                        size="small"
                        v-tooltip.top="cert.isDraft ? 'Publiceren' : 'Depubliceren'"
                        @click="confirmToggleCertPublish(cert)"
                      />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </TabPanel>
    </Tabs>
  </div>
</template>
